import { DragEndEvent, DragStartEvent } from '@dnd-kit/core';
import { useState } from 'react';
import { UseFieldArrayReplace, UseFieldArrayUpdate, useFormContext } from 'react-hook-form';
import { ClassificationDTO } from '../../api/useAssetBucketQuery';
import { FormFieldValues } from '../../AssetBucketing.meta';

interface Params {
  updateBucket: UseFieldArrayUpdate<FormFieldValues, 'buckets'>;
  replaceBucket: UseFieldArrayReplace<FormFieldValues, 'buckets'>;
}

export const useBucketList = ({ updateBucket, replaceBucket }: Params) => {
  const { watch, setValue } = useFormContext<FormFieldValues>();

  const buckets = watch('buckets');
  const unassignedClassifications = watch('unassignedClassifications');

  const [activeAsset, setActiveAsset] = useState<ClassificationDTO | null>(null);

  const handleDragEnd = (event: DragEndEvent) => {
    const { over, active } = event;

    if (over?.data.current && active.data.current) {
      const { bucketIndex } = over.data.current;
      const {
        id: activeClassificationId,
        data: {
          current: { classification, bucketIndex: oldBucketIndex },
        },
      } = active;

      const newFields = buckets.map((field, index) => {
        // Add to the bucket
        if (index === bucketIndex) {
          const isClassificationInTheBucket = field.classifications.find(
            ({ classificationId }) => classificationId === classification.classificationId,
          );

          return {
            ...field,
            classifications: isClassificationInTheBucket
              ? field.classifications
              : [...field.classifications, classification],
          };
        }

        // Remove from old bucket
        if (index === oldBucketIndex) {
          return {
            ...field,
            classifications: field.classifications.filter(
              ({ classificationId }) => `${classificationId}` !== activeClassificationId,
            ),
          };
        }

        return field;
      });

      replaceBucket(newFields);

      setValue(
        'unassignedClassifications',
        unassignedClassifications.filter(
          ({ classificationId }) => `${classificationId}` !== activeClassificationId,
        ),
      );
    }

    setActiveAsset(null);
  };

  const removeItem = ({
    classification,
    bucketIndex,
  }: {
    classification: ClassificationDTO;
    bucketIndex: number;
  }) => {
    updateBucket(bucketIndex, {
      ...buckets[bucketIndex],
      classifications: buckets[bucketIndex].classifications.filter(
        ({ classificationId }) => classificationId !== classification.classificationId,
      ),
    });

    setValue('unassignedClassifications', [...unassignedClassifications, classification]);
  };

  const handleDragStart = (event: DragStartEvent) => {
    setActiveAsset(event.active.data.current?.classification);
  };

  return {
    data: {
      activeAsset,
      unassignedClassifications,
    },
    actions: {
      handleDragEnd,
      handleDragStart,
      removeItem,
    },
  };
};
